import { useMutation } from '@apollo/client';
import { useState } from 'react';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { CREATE_VOUCH_MUTATION } from '../../../graphql/resolvers/vouchMutations/createVouch.gql';
import { useMentorships } from '../../../hooks/useMentorships';
import { VOUCH_COPY, vouchErrorMessage } from '../vouch.copy';
import './VouchConfirm.scss';

interface Props {
  target: { userId: string; username: string };
  available: number;
  onVouched: () => void;
  onCancel: () => void;
}

export function VouchConfirm({
  target,
  available,
  onVouched,
  onCancel,
}: Props) {
  const [credits, setCredits] = useState(1);
  const [grantedBoost, setGrantedBoost] = useState<number | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [createVouch, { loading }] = useMutation(CREATE_VOUCH_MUTATION);
  const { mentorships } = useMentorships();

  // Proactively reflect the server's ALREADY_VOUCHING guard: a LIVE (non-revoked)
  // active vouch for this person blocks a new one. A revoked/winding-down vouch is
  // excluded (TRIL-401) — its boost has ended, so you can vouch for them again.
  const alreadyVouching = mentorships.some(
    (m) =>
      m.status === 'ACTIVE' &&
      !m.revokedAt &&
      m.recipientUsername === target.username,
  );

  const submit = async () => {
    setErrorCode(null);
    try {
      const res = await createVouch({
        variables: { recipientUserId: target.userId, credits },
      });
      const b = res.data?.createVouch.boost;
      setGrantedBoost(typeof b === 'number' && b > 0 ? b : null);
      onVouched();
    } catch (e: unknown) {
      const err = e as {
        graphQLErrors?: Array<{ extensions?: { code?: string } }>;
        message?: string;
      };
      setErrorCode(
        String(
          err?.graphQLErrors?.[0]?.extensions?.code ??
            err?.message ??
            'VOUCH_FAILED',
        ),
      );
    }
  };

  if (grantedBoost !== null) {
    return (
      <div className="vouch-confirm">
        <p className="vouch-confirm__success">
          {VOUCH_COPY.confirmSuccess(grantedBoost)}
        </p>
        <ButtonComponent variant="primary" onClick={onCancel}>
          {VOUCH_COPY.confirmDone}
        </ButtonComponent>
      </div>
    );
  }

  if (alreadyVouching) {
    return (
      <div className="vouch-confirm">
        <p className="vouch-confirm__target">
          {VOUCH_COPY.confirmTarget(target.username)}
        </p>
        <p className="vouch-confirm__notice">
          {vouchErrorMessage('ALREADY_VOUCHING')}
        </p>
        <div className="vouch-confirm__actions">
          <ButtonComponent variant="outline" onClick={onCancel}>
            {VOUCH_COPY.confirmDone}
          </ButtonComponent>
        </div>
      </div>
    );
  }

  return (
    <div className="vouch-confirm">
      <p className="vouch-confirm__target">
        {VOUCH_COPY.confirmTarget(target.username)}
      </p>
      <label className="vouch-confirm__field">
        {VOUCH_COPY.creditsLabel}
        <input
          type="number"
          min={1}
          max={available}
          value={credits}
          step={1}
          onChange={(e) =>
            setCredits(
              Math.max(1, Math.min(available, Number(e.target.value) || 1)),
            )
          }
        />
      </label>
      <p className="vouch-confirm__terms">{VOUCH_COPY.confirmTerms}</p>
      {errorCode && (
        <p className="vouch-confirm__error" role="alert">
          {vouchErrorMessage(errorCode)}
        </p>
      )}
      <div className="vouch-confirm__actions">
        <ButtonComponent variant="outline" onClick={onCancel}>
          Cancel
        </ButtonComponent>
        <ButtonComponent
          variant="primary"
          onClick={submit}
          disabled={loading || available < 1}
        >
          {loading
            ? VOUCH_COPY.confirmLoading
            : `${VOUCH_COPY.confirmButton} (${credits})`}
        </ButtonComponent>
      </div>
    </div>
  );
}
